// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductNonReturnable, { ProductNonReturnableStyles } from "@west/App/Components/Product/ProductNonReturnable";
import UomMessage, { UomMessageStyles } from "@west/App/Components/Product/UomMessage";
import {
    AuthenticationPrompt,
    AuthenticationPromptStyles,
} from "@west/App/Widgets/AuthenticationPrompt/AuthenticationPrompt";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => {
    return {
        configurationSelection: state.pages.productDetails.configurationSelection,
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        subscription: state.pages.productDetails.subscription,
    };
};

export interface ProductDetailsAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    labelValueWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    labelTypo?: TypographyPresentationProps;
    valueTypo?: TypographyPresentationProps;
    uomMessageStyles?: UomMessageStyles;
    productNonReturnable?: ProductNonReturnableStyles;
    authenticationPrompt?: AuthenticationPromptStyles;
    qtyDetailsWrapper?: InjectableCss;
}

export const addToCartButtonStyles: ProductDetailsAddToCartButtonStyles = {
    button: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = addToCartButtonStyles;

const ProductDetailsAddToCartButton = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    product,
}: Props) => {
    return (
        <StyledWrapper {...styles.container}>
            <UomMessage extendedStyled={styles.uomMessageStyles} />
            <ProductNonReturnable extendedStyled={styles.productNonReturnable} />
            <StyledWrapper {...styles.qtyDetailsWrapper}>
                {product.minimumOrderQty > 1 && (
                    <StyledWrapper {...styles.labelValueWrapper}>
                        <Typography {...styles.labelTypo}>{translate("Min. Qty")}: </Typography>
                        <Typography {...styles.valueTypo}>{product.minimumOrderQty}</Typography>
                    </StyledWrapper>
                )}
                {product.detail?.multipleSaleQty !== undefined && product.detail?.multipleSaleQty > 1 && (
                    <StyledWrapper {...styles.labelValueWrapper}>
                        <Typography {...styles.labelTypo}>{translate("Qty Interval")}: </Typography>
                        <Typography {...styles.valueTypo}>{product.detail?.multipleSaleQty}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                <ProductQuantityOrdered
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    extendedStyles={styles.quantityOrdered}
                />
                <ProductAddToCartButton
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                    extendedStyles={styles.button}
                    data-test-selector="addProductToCart"
                />
            </StyledWrapper>
            <AuthenticationPrompt extendedStyles={styles.authenticationPrompt} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsAddToCartButton)),
    definition: {
        displayName: "Add to Cart Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
